import React, { useState } from 'react';
import { Mail, MapPin, Clock, Send, CheckCircle } from 'lucide-react';
import Button from '../../components/Button/Button';
import Card from '../../components/Card/Card';
import './Contact.css';

const Contact: React.FC = () => {

  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    company: '',
    currentRole: '',
    service: '',
    message: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Send email via Vercel serverless function
      const response = await fetch('/api/send-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error('Failed to send email');
      }

      setIsSubmitting(false);
      setIsSubmitted(true);

      // Reset form after 5 seconds
      setTimeout(() => {
        setIsSubmitted(false);
        setFormData({
          fullName: '',
          email: '',
          phone: '',
          company: '',
          currentRole: '',
          service: '',
          message: '',
        });
      }, 5000);
    } catch (error) {
      console.error('Error sending email:', error);
      alert('Failed to send message. Please try again or email us directly at info@baselineconsulting.org');
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: <Mail size={24} />,
      title: 'Email',
      content: 'info@baselineconsulting.org',
      link: 'mailto:info@baselineconsulting.org',
    },
    {
      icon: <MapPin size={24} />,
      title: 'Headquarters',
      content: 'Global Operations',
      link: null,
    },
    {
      icon: <Clock size={24} />,
      title: 'Business Hours',
      content: 'Mon-Fri: 9AM-6PM EST',
      link: null,
    },
  ];

  return (
    <div className="contact">
      {/* Hero Section */}
      <section className="contact-hero">
        <div className="container">
          <div className="contact-hero-content">
            <h1 className="contact-hero-title">Get in Touch</h1>
            <p className="contact-hero-subtitle">
              Let's discuss how we can help transform your racquet sports business
            </p>
          </div>
        </div>
      </section>

      {/* Main Contact Section */}
      <section className="section contact-main">
        <div className="container">
          <div className="contact-grid">
            {/* Contact Form */}
            <div className="contact-form-wrapper">
              <Card variant="bordered">
                <div className="contact-form-container">
                  <h2 className="contact-form-title">Send Us a Message</h2>
                  <p className="contact-form-subtitle">
                    Fill out the form below and we'll get back to you within 24 hours
                  </p>

                  {isSubmitted ? (
                    <div className="form-success">
                      <CheckCircle size={48} className="success-icon" />
                      <h3>Thank You!</h3>
                      <p>Your message has been sent successfully. We'll be in touch soon.</p>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="contact-form">
                      <div className="form-grid">
                        <div className="form-group">
                          <label htmlFor="fullName">Full Name *</label>
                          <input
                            type="text"
                            id="fullName"
                            name="fullName"
                            value={formData.fullName}
                            onChange={handleInputChange}
                            required
                            placeholder="John Doe"
                          />
                        </div>

                        <div className="form-group">
                          <label htmlFor="email">Email Address *</label>
                          <input
                            type="email"
                            id="email"
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            required
                            placeholder="john@example.com"
                          />
                        </div>

                        <div className="form-group">
                          <label htmlFor="phone">Phone Number</label>
                          <input
                            type="tel"
                            id="phone"
                            name="phone"
                            value={formData.phone}
                            onChange={handleInputChange}
                            placeholder="+1 (234) 567-890"
                          />
                        </div>

                        <div className="form-group">
                          <label htmlFor="company">Company/Club Name</label>
                          <input
                            type="text"
                            id="company"
                            name="company"
                            value={formData.company}
                            onChange={handleInputChange}
                            placeholder="Tennis Club Name"
                          />
                        </div>

                        <div className="form-group">
                          <label htmlFor="currentRole">Current Role *</label>
                          <input
                            type="text"
                            id="currentRole"
                            name="currentRole"
                            value={formData.currentRole}
                            onChange={handleInputChange}
                            required
                            placeholder="e.g., Tennis Director, Club Manager"
                          />
                        </div>
                      </div>

                      <div className="form-group">
                        <label htmlFor="service">Service of Interest</label>
                        <select
                          id="service"
                          name="service"
                          value={formData.service}
                          onChange={handleInputChange}
                        >
                          <option value="">Select a service</option>
                          <option value="online-coaching">Online Coaching</option>
                          <option value="club-consulting">Club Consulting</option>
                          <option value="corporate-retreats">Corporate Retreats</option>
                          <option value="other">Other</option>
                        </select>
                      </div>

                      <div className="form-group">
                        <label htmlFor="message">Message *</label>
                        <textarea
                          id="message"
                          name="message"
                          value={formData.message}
                          onChange={handleInputChange}
                          required
                          rows={6}
                          placeholder="Tell us about your project or how we can help..."
                        />
                      </div>

                      <Button
                        type="submit"
                        variant="primary"
                        size="large"
                        disabled={isSubmitting}
                        fullWidth
                      >
                        {isSubmitting ? (
                          'Sending...'
                        ) : (
                          <>
                            Send Message
                            <Send size={20} style={{ marginLeft: '8px' }} />
                          </>
                        )}
                      </Button>
                    </form>
                  )}
                </div>
              </Card>
            </div>

            {/* Contact Information */}
            <div className="contact-info-wrapper">
              <div className="contact-info-header">
                <h2>Let's Start a Conversation</h2>
                <p>
                  Whether you're looking to transform your club or enhance your career in racquet sports, 
                  we're here to help.
                </p>
              </div>

              <div className="contact-info-grid">
                {contactInfo.map((item) => (
                  <div key={item.title} className="contact-info-item">
                    <div className="contact-info-icon">{item.icon}</div>
                    <div className="contact-info-content">
                      <h3>{item.title}</h3>
                      {item.link ? (
                        <a href={item.link}>{item.content}</a>
                      ) : (
                        <p>{item.content}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <Card variant="elevated" className="consultation-card">
                <div className="consultation-content">
                  <h3>Free Consultation</h3>
                  <p>
                    Schedule a free 30-minute consultation to discuss your needs and explore how 
                    Baseline can help you achieve your goals.
                  </p>
                  <Button variant="secondary" size="medium" href="https://calendar.app.google/zeaz1ocNpttMocbv7" fullWidth>
                    Book a Call
                  </Button>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="section faq-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title text-center">Frequently Asked Questions</h2>
          </div>

          <div className="faq-grid">
            <div className="faq-item">
              <h3>What is your typical engagement timeline?</h3>
              <p>
                Project timelines vary based on scope and complexity. Online coaching can begin 
                immediately, while consulting projects typically range from 3-12 months.
              </p>
            </div>

            <div className="faq-item">
              <h3>Do you work with clubs of all sizes?</h3>
              <p>
                Yes! We work with everything from small private clubs to large club 
                operations. Our services are customized to meet the unique needs and budget of 
                each member.
              </p>
            </div>

            <div className="faq-item">
              <h3>How do I get started?</h3>
              <p>
                Simply fill out the contact form above or give us a call. We'll schedule an initial 
                consultation to understand your needs and propose a customized solution that fits 
                your goals and budget.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
